import axios from '../lib/axios';
import { showToast } from '../utils/toast';

// Cache configuration
const CACHE_DURATION = 1000 * 60 * 30; // 30 minutes
const cache: Record<string, { data: any, timestamp: number }> = {};

// Merchant interface
export interface Merchant {
  _id: string;
  mobileNo: string;
  merchantId: string;
  afpId: string;
  isEv: boolean;
  partnerisEv: boolean;
  img: string;
  kycShop: {
    name: string;
    address: string;
    state: string;
    city: string;
    pin: string;
    note: string;
  };
  kycBank: {
    accountNo: string;
    ifsc: string;
    holderName: string;
  };
  kycDocs: {
    shopFront: string;
    shopInner: string;
    evCer: string;
  };
  suspended: boolean;
  activated: boolean;
  kyc: {
    status: boolean;
    bank: boolean;
    shop: boolean;
    docs: boolean;
  };
  verified: boolean;
  eVerified: boolean;
  activatedBy: string | null;
  lastLogin: string;
  createdAt: string;
  updatedAt: string;
  email: string;
  kycGstNo: string;
  name: string;
  serialNumber?: number;
}

interface BulkUploadResponse {
  total: number;
  created: number;
  skippedRowsNo: number;
  skippedEntries: {
    row: number;
    reason: string;
  }[];
  skippedFile?: string;
}

interface CreateMerchantPayload {
  name: string;
  mobileNo: string;
  email?: string;
  afpId: string;
  kyc: string;
  kycShop: {
    address: string;
    city: string;
    state: string;
    pin: string;
  }
}

interface CreateMerchantResponse {
  message: string;
  data: Merchant;
}


// API response interface
interface MerchantApiResponse {
  status: string;
  message?: string;
  result?: {
    pagination: {
      totalCount: number;
      totalMatch: number;
      page: number;
      pageSize: number;
      totalPages: number;
    };
    data: Merchant[];
  };
}

// Filter interface
export interface MerchantFilters {
  group?: string;
  kycStatus?: 'all' | 'Verified' | 'NotVerified' | 'Not Verified' | 'KycPending';
  dateRange?: {
    from?: string | undefined;
    to?: string | undefined
  };
}

/**
 * Fetch merchants with pagination, sorting, filtering and search
 * @param token Authentication token
 * @param page Current page number
 * @param pageSize Number of items per page
 * @param searchQuery Search query for filtering
 * @param sortBy Field to sort by
 * @param sortOrder Sort direction (asc or desc)
 * @param filters Additional filters for merchants
 * @returns Promise with merchants data and pagination info
 */
export const fetchMerchants = async ({
  page = 1,
  pageSize = 10,
  sortField = "",
  sortOrder = "",
  search = "",
  token = "",
  filters = {}
}: {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  search?: string;
  token?: string | null;
  filters?: MerchantFilters;
}): Promise<{
  merchants: Merchant[];
  totalMatch: number;
  pageNo: number;
  pageSize: number;
  paginationMessage?: string;
}> => {
  try {
    let authToken = token || localStorage.getItem("token") || "";

    // Build query parameters
    const params = new URLSearchParams();
    params.append("page", String(page));
    params.append("pageSize", String(pageSize));
    if (sortField) params.append("sortField", sortField);
    if (sortOrder) params.append("sortOrder", sortOrder);
    if (search) params.append("search", search);

    // Add filter parameters
    if (filters.group) params.append("afpId", filters.group);

    if (filters.kycStatus && filters.kycStatus !== 'all') {
      // Convert "Not Verified" to "NotVerified" for the API
      const kycStatusValue = filters.kycStatus === 'Not Verified' ? 'NotVerified' : filters.kycStatus;
      params.append("kycStatus", kycStatusValue);
    }

    // Date filters use 'from' and 'to' params in DD-MM-YYYY format
    if (filters.dateRange?.from) params.append("from", formatDateForAPI(filters.dateRange.from));
    if (filters.dateRange?.to) params.append("to", formatDateForAPI(filters.dateRange.to));

    console.log(`[API] Fetching merchants with filters:`, {
      page,
      pageSize,
      sortField,
      sortOrder,
      search,
      filters,
      formattedDates: {
        from: filters.dateRange?.from ? formatDateForAPI(filters.dateRange.from) : undefined,
        to: filters.dateRange?.to ? formatDateForAPI(filters.dateRange.to) : undefined
      }
    });

    const url = `/adminusers/v1/merchant?${params.toString()}`;
    console.log(`[API] Request URL: ${url}`);

    const response = await axios.get<MerchantApiResponse>(url, {
      headers: {
        "Content-Type": "application/json",
        token: authToken,
      },
    });

    console.log("MERCHANT API RESPONSE:", response.data);

    if (
      response.data?.status !== "Request successful!" ||
      !response.data.result
    ) {
      throw new Error("Unexpected API response structure");
    }

    // Handle the response structure
    const { pagination, data } = response.data.result;

    if (!Array.isArray(data)) {
      console.error("Data is not an array:", data);
      throw new Error("Invalid data format in API response");
    }

    let merchants: Merchant[] = data;
    let totalMatch = pagination.totalMatch;
    let pageNo = pagination.page;
    let actualPageSize = pagination.pageSize;
    let paginationMessage = `Server-side pagination is active. Total pages: ${pagination.totalPages}`;

    // Add serial numbers to merchants based on their position in the paginated results
    merchants = merchants.map((merchant, index) => {
      const serialNumber = (pageNo - 1) * actualPageSize + index + 1;
      return {
        ...merchant,
        serialNumber
      };
    });

    console.log(`Server pagination: Page ${pageNo} of ${pagination.totalPages}, showing ${merchants.length} of ${totalMatch} total merchants`);

    return {
      merchants,
      totalMatch,
      pageNo,
      pageSize: actualPageSize,
      paginationMessage
    };
  } catch (error: any) {
    console.error("Error fetching merchants:", error);
    throw error;
  }
};

// Helper function to format dates for the API
function formatDateForAPI(dateString: string): string {
  try {
    // If the input is already in 'DD-MM-YYYY' format, return as is
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateString)) {
      return dateString;
    }

    // If it's in ISO or 'YYYY-MM-DD' format, convert to 'DD-MM-YYYY'
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original if not a valid date
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (e) {
    console.error('Error formatting date:', e);
    return dateString; // Return original in case of error
  }
}

/**
 * Fetch a single merchant by ID
 * @param id Merchant ID
 * @param token Authentication token
 * @returns Promise with merchant data
 */
export const fetchMerchantById = async (
  id: string,
  token: string | null
): Promise<Merchant> => {
  try {
    console.log(`[API] Fetching merchant with ID: ${id}`);

    const response = await axios.get<MerchantApiResponse>(`/adminusers/v1/merchant/${id}`, {
      headers: {
        'Content-Type': 'application/json',
        'token': token || ''
      }
    });

    console.log('[API] Merchant detail response:', response.data);

    if (response.data?.status === "Request successful!" && response.data.result) {
      // Handle different possible response formats
      if (Array.isArray(response.data.result)) {
        // If result is an array, take the first item
        return response.data.result[0];
      } else if (response.data.result.data && Array.isArray(response.data.result.data)) {
        // If result has a data property that is an array
        return response.data.result.data[0];
      } else {
        // Direct object
        return response.data.result as unknown as Merchant;
      }
    }

    throw new Error('Invalid response format from API');
  } catch (error: any) {
    console.error('[API] Error fetching merchant:', error);
    const errorMessage = error.response?.data?.message || 'Failed to fetch merchant details';
    showToast.error(errorMessage);

    throw error;
  }
};

/**
 * Delete a merchant using any available identifier
 * @param identifier Either a merchant object or ID/mobile/name string
 * @param token Authentication token
 * @param identifierType Optional type to specify which identifier is being used
 * @returns Promise indicating success
 */
export const deleteMerchant = async (
  identifier: Merchant | string,
  token: string | null,
  identifierType?: 'id' | 'mobile' | 'name'
): Promise<boolean> => {
  try {
    console.log(`[MERCHANT-DELETE] Delete merchant function called with token present: ${!!token}`);
    console.log(`[MERCHANT-DELETE] Identifier type: ${typeof identifier === 'string' ? 'string' : 'merchant object'}`);

    // Check if token is missing
    if (!token) {
      console.error('[MERCHANT-DELETE] Missing authentication token');
      showToast.error('Authentication required to delete merchant');
      return false;
    }

    // If identifier is a full merchant object
    if (typeof identifier !== 'string') {
      const merchant = identifier;
      console.log(`[MERCHANT-DELETE] Attempting to delete merchant:`, {
        id: merchant._id,
        name: merchant.name,
        mobile: merchant.mobileNo
      });

      // Ensure we have a valid identifier to work with
      if (!merchant._id && !merchant.mobileNo && !merchant.name) {
        console.error('[MERCHANT-DELETE] No valid identifier found in merchant object');
        showToast.error('Cannot delete merchant: No valid identifier found');
        return false;
      }

      let lastError = null;

      // Try deletion by mobile number
      if (merchant.mobileNo) {
        try {
          console.log(`[MERCHANT-DELETE] Attempting deletion by mobile number: ${merchant.mobileNo}`);
          const endpoint = `/adminusers/v1/merchant/mobile/${merchant.mobileNo}`;
          console.log(`[MERCHANT-DELETE] Mobile endpoint: ${endpoint}`);

          const response = await axios.delete(endpoint, {
            headers: {
              'Content-Type': 'application/json',
              'token': token
            }
          });

          console.log(`[MERCHANT-DELETE] Mobile deletion response:`, {
            status: response.status,
            data: response.data
          });

          if (response.data?.status === "Request successful!") {
            showToast.success('Merchant terminated successfully by mobile number');
            return true;
          } else {
            console.warn(`[MERCHANT-DELETE] Deletion by mobile returned success=false:`, response.data);
          }
        } catch (error: any) {
          console.error(`[MERCHANT-DELETE] Deletion by mobile failed:`, error.response?.data || error.message);
          lastError = error;
          console.log(`[MERCHANT-DELETE] Attempting next method...`);
        }
      }

      // Try deletion by name
      if (merchant.name) {
        try {
          console.log(`[MERCHANT-DELETE] Attempting deletion by name: ${merchant.name}`);
          const endpoint = `/adminusers/v1/merchant/name/${encodeURIComponent(merchant.name)}`;
          console.log(`[MERCHANT-DELETE] Name endpoint: ${endpoint}`);

          const response = await axios.delete(endpoint, {
            headers: {
              'Content-Type': 'application/json',
              'token': token
            }
          });

          console.log(`[MERCHANT-DELETE] Name deletion response:`, {
            status: response.status,
            data: response.data
          });

          if (response.data?.status === "Request successful!") {
            showToast.success('Merchant terminated successfully by name');
            return true;
          } else {
            console.warn(`[MERCHANT-DELETE] Deletion by name returned success=false:`, response.data);
          }
        } catch (error: any) {
          console.error(`[MERCHANT-DELETE] Deletion by name failed:`, error.response?.data || error.message);
          lastError = error;
          console.log(`[MERCHANT-DELETE] Attempting next method...`);
        }
      }

      // Try deletion by ID
      if (merchant._id) {
        try {
          console.log(`[MERCHANT-DELETE] Attempting deletion by ID: ${merchant._id}`);
          const endpoint = `/adminusers/v1/merchant/${merchant._id}`;
          console.log(`[MERCHANT-DELETE] ID endpoint: ${endpoint}`);

          const response = await axios.delete(endpoint, {
            headers: {
              'Content-Type': 'application/json',
              'token': token
            }
          });

          console.log(`[MERCHANT-DELETE] ID deletion response:`, {
            status: response.status,
            data: response.data
          });

          if (response.data?.status === "Request successful!") {
            showToast.success('Merchant terminated successfully by ID');
            return true;
          } else {
            console.warn(`[MERCHANT-DELETE] Deletion by ID returned success=false:`, response.data);
          }
        } catch (error: any) {
          console.error(`[MERCHANT-DELETE] Deletion by ID failed:`, error.response?.data || error.message);
          lastError = error;
          // This is our last attempt, so we'll handle error display here
          const errorMessage = error.response?.data?.message || 'Failed to terminate merchant';
          showToast.error(errorMessage);
          return false;
        }
      }

      console.error('[MERCHANT-DELETE] Failed to terminate merchant after trying all methods');
      showToast.error('Failed to terminate merchant after trying all methods');
      return false;
    }
    // If identifier is a string (id, mobile, or name)
    else {
      const identifierValue = identifier;
      let endpoint = '/adminusers/v1/merchant';

      // Determine which endpoint to use based on identifierType
      if (identifierType === 'mobile') {
        endpoint += `/mobile/${identifierValue}`;
        console.log(`[API] Deleting merchant with mobile: ${identifierValue}`);
      } else if (identifierType === 'name') {
        endpoint += `/name/${encodeURIComponent(identifierValue)}`;
        console.log(`[API] Deleting merchant with name: ${identifierValue}`);
      } else {
        // Default to ID
        endpoint += `/${identifierValue}`;
        console.log(`[API] Deleting merchant with ID: ${identifierValue}`);
      }

      try {
        const response = await axios.delete(endpoint, {
          headers: {
            'Content-Type': 'application/json',
            'token': token
          }
        });

        console.log('[API] Delete merchant response:', response.data);

        if (response.data?.status === "Request successful!") {
          showToast.success('Merchant terminated successfully');
          return true;
        } else {
          console.warn('[API] Delete merchant returned success=false:', response.data);
          showToast.error(response.data?.message || 'Failed to terminate merchant');
          return false;
        }
      } catch (error: any) {
        console.error('[API] Error terminating merchant:', error.response?.data || error.message);
        const errorMessage = error.response?.data?.message || 'Failed to terminate merchant';
        showToast.error(errorMessage);
        return false;
      }
    }
  } catch (error: any) {
    console.error('[API] Error in deleteMerchant function:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to terminate merchant';
    showToast.error(errorMessage);

    return false;
  }
};

/**
 * Delete a merchant document
 * @param merchantIdentifier Merchant ID or mobile number
 * @param documentType Document type to delete (shopFront, shopInner, evCer)
 * @param token Authentication token
 * @param isUsingMobile Flag to indicate if using mobile number instead of ID
 * @returns Promise indicating success
 */
export const deleteDocument = async (
  merchantIdentifier: string,
  documentType: string,
  token: string | null,
  isUsingMobile: boolean = false
): Promise<boolean> => {
  try {
    console.log(`[API] Deleting document ${documentType} for merchant ${isUsingMobile ? 'mobile' : 'ID'}: ${merchantIdentifier}`);

    if (!token) {
      console.error('[API] Missing authentication token for document deletion');
      showToast.error('Authentication required to delete document');
      return false;
    }

    if (!merchantIdentifier || !documentType) {
      console.error('[API] Missing required parameters for document deletion');
      showToast.error('Missing required information for document deletion');
      return false;
    }

    try {
      let endpoint = '';

      if (isUsingMobile) {
        // API path for deletion by mobile number: /users/merchant/docs/{docType}/{mobileNo}
        endpoint = `/adminusers/merchant/docs/${documentType}/${merchantIdentifier}`;
      } else {
        // API path for deletion by merchant ID
        endpoint = `/adminusers/merchant/docs/${documentType}/${merchantIdentifier}`;
      }

      console.log(`[API] Using endpoint: ${endpoint}`);

      const response = await axios.delete(endpoint, {
        headers: {
          'Content-Type': 'application/json',
          'token': token
        }
      });

      console.log('[API] Delete document response:', response.data);

      if (response.data?.status === "Request successful!") {
        showToast.success('Document deleted successfully');

        // After successfully deleting the document, check if all documents are now deleted
        // If all are deleted, then update the merchant's KYC docs flag to false
        try {
          // Check if any other documents still exist
          const docTypes = ['shopFront', 'shopInner', 'evCer'];
          const otherTypes = docTypes.filter(type => type !== documentType);

          let allDocsDeleted = true;

          // Try to fetch each of the other document types
          for (const type of otherTypes) {
            try {
              const checkEndpoint = `/adminusers/merchant/docs/${type}/${merchantIdentifier}`;
              const checkResponse = await axios.get(checkEndpoint, {
                headers: {
                  'Content-Type': 'application/json',
                  'token': token
                }
              });

              // If we successfully get a URL, at least one document still exists
              if (checkResponse.data?.status === "Request successful!" &&
                checkResponse.data.result?.url) {
                console.log(`[API] Document still exists: ${type}`);
                allDocsDeleted = false;
                break;
              }
            } catch (checkError: any) {
              // If the error is a 404 Not Found, it means the document doesn't exist
              if (checkError.response?.status === 404) {
                console.log(`[API] Document doesn't exist: ${type}`);
              } else {
                // For other errors, we assume the document might still exist
                console.error(`[API] Error checking document ${type}:`, checkError);
                allDocsDeleted = false;
                break;
              }
            }
          }

          // Only update the kyc.docs flag if all documents are deleted
          if (allDocsDeleted) {
            console.log('[API] All documents have been deleted, updating kyc.docs flag to false');

            // Determine which endpoint to use for the update, using query parameters like in partner service
            const updateEndpoint = isUsingMobile
              ? `/adminusers/v1/merchant?mobileNo=${merchantIdentifier}`
              : `/adminusers/v1/merchant?id=${merchantIdentifier}`;

            // The update payload needs to set kyc.docs to false
            const updatePayload = {
              kyc: {
                docs: false
              }
            };

            console.log(`[API] Updating merchant KYC docs flag at endpoint: ${updateEndpoint}`);

            // Call the update endpoint
            const updateResponse = await axios.put(updateEndpoint, updatePayload, {
              headers: {
                'Content-Type': 'application/json',
                'token': token
              }
            });

            console.log('[API] Merchant KYC flag update response:', updateResponse.data);

            if (updateResponse.data?.status === "Request successful!" ||
              updateResponse.data?.status === "Updated!") {
              console.log('[API] Successfully updated merchant KYC docs flag to false');
            } else {
              console.warn('[API] Failed to update merchant KYC docs flag:', updateResponse.data);
            }
          } else {
            console.log('[API] Some documents still exist, keeping kyc.docs flag as true');
          }
        } catch (updateError) {
          console.error('[API] Error checking or updating merchant KYC docs flag:', updateError);
          // We don't fail the overall operation if just the flag update fails
        }

        return true;
      } else {
        console.warn('[API] Delete document returned success=false:', response.data);
        showToast.error(response.data?.message || 'Failed to delete document');
        return false;
      }
    } catch (error: any) {
      console.error('[API] Error deleting document:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || 'Failed to delete document';
      showToast.error(errorMessage);
      return false;
    }
  } catch (error: any) {
    console.error('[API] Error in deleteDocument function:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Failed to delete document';
    showToast.error(errorMessage);

    return false;
  }
};

// /**
//  * Fetch all unique merchant groups from all merchants
//  * @param token Auth token
//  * @returns Unique group names
//  */
// export const fetchMerchantGroups = async (
//   token: string | null
// ): Promise<string[]> => {
//   try {
//     console.log('[API] Fetching all merchants to extract all unique groups');

//     // Fetch all merchants in one request with a large page size
//     const response = await axios.get<MerchantApiResponse>('/adminusers/v1/merchant', {
//       params: {
//         page: 1,
//         pageSize: 1000, // Large page size to get all merchants at once
//       },
//       headers: {
//         'Content-Type': 'application/json',
//         token: token || ''
//       },
//       timeout: 30000 // Longer timeout for this larger request
//     });

//     if (
//       response.data?.status !== 'Request successful!' ||
//       !response.data.result
//     ) {
//       throw new Error('Unexpected API response');
//     }

//     // Extract merchants from the correct location in the response
//     let merchants: Merchant[] = [];
//     if (response.data.result.data && Array.isArray(response.data.result.data)) {
//       merchants = response.data.result.data;
//       console.log(`[API] Retrieved ${merchants.length} merchants for group extraction`);
//     } else {
//       console.error('Unexpected merchant data format:', response.data.result);
//       throw new Error('Invalid merchant data format');
//     }

//     // Extract all groups and filter out empty values
//     const groups = merchants
//       .map(merchant => merchant.afpId)
//       .filter((group): group is string => typeof group === 'string' && group.trim() !== '');

//     // Remove duplicates and sort
//     const uniqueGroups = Array.from(new Set(groups)).sort();

//     console.log(`[API] Extracted all ${uniqueGroups.length} unique merchant groups:`, uniqueGroups);

//     // Cache the complete list of groups in localStorage
//     try {
//       localStorage.setItem('merchantGroups', JSON.stringify(uniqueGroups));
//       localStorage.setItem('merchantGroupsTimestamp', Date.now().toString());
//       console.log('[API] Cached all unique merchant groups for future use');
//     } catch (e) {
//       console.warn('[API] Failed to cache merchant groups:', e);
//     }

//     return uniqueGroups;
//   } catch (error: any) {
//     // Handle timeout specifically
//     if (error.code === 'ECONNABORTED') {
//       console.error('[API] Timeout while fetching all merchant groups:', error);
//       showToast.error('Timeout while fetching merchant groups. Using cached data if available.');

//       // Return cached groups if available
//       const cachedGroups = localStorage.getItem('merchantGroups');
//       if (cachedGroups) {
//         try {
//           const groups = JSON.parse(cachedGroups);
//           const timestamp = localStorage.getItem('merchantGroupsTimestamp');
//           const cacheAge = timestamp ? Math.floor((Date.now() - parseInt(timestamp)) / (1000 * 60 * 60)) : 0;

//           console.log(`[API] Using cached merchant groups from ${cacheAge} hours ago:`, groups);
//           return groups;
//         } catch (e) {
//           console.error('[API] Failed to parse cached merchant groups:', e);
//           return [];
//         }
//       }
//       return [];
//     }

//     console.error('[API] Error fetching all merchant groups:', error);
//     showToast.error('Failed to fetch merchant groups');
//     return [];
//   }
// };

/**
 * Fetch a merchant document URL by mobile number or ID
 * @param merchantIdentifier Merchant ID or mobile number
 * @param documentType Document type to fetch (shopFront, shopInner, evCer)
 * @param token Authentication token
 * @param isUsingMobile Flag to indicate if using mobile number instead of ID
 * @returns Promise with document URL
 */
export const fetchMerchantDocument = async (
  merchantIdentifier: string,
  documentType: string,
  token: string | null,
  isUsingMobile: boolean = false
): Promise<string | null> => {
  try {
    console.log(`[API] Fetching document ${documentType} for merchant ${isUsingMobile ? 'mobile' : 'ID'}: ${merchantIdentifier}`);

    if (!token) {
      console.error('[API] Missing authentication token for document fetch');
      showToast.error('Authentication required to view document');
      return null;
    }

    if (!merchantIdentifier || !documentType) {
      console.error('[API] Missing required parameters for document fetch');
      showToast.error('Missing required information for document fetch');
      return null;
    }

    let endpoint = '';

    if (isUsingMobile) {
      // API path for fetching by mobile number: /users/merchant/docs/{docType}/{mobileNo}
      endpoint = `/adminusers/merchant/docs/${documentType}/${merchantIdentifier}`;
    } else {
      // API path for fetching by merchant ID
      endpoint = `/adminusers/v1/merchant/${merchantIdentifier}/document/${documentType}`;
    }

    console.log(`[API] Using endpoint: ${endpoint}`);

    const response = await axios.get(endpoint, {
      headers: {
        'Content-Type': 'application/json',
        'token': token
      }
    });

    console.log('[API] Fetch document response:', response.data);

    if (response.data?.status === "Request successful!" && response.data.result?.url) {
      return response.data.result.url;
    } else {
      console.warn('[API] Fetch document returned without URL:', response.data);
      // showToast.error(response.data?.message || 'Failed to fetch document');
      return null;
    }
  } catch (error: any) {
    console.error('[API] Error fetching document:', error.response?.data || error.message);
    const errorMessage = error.response?.data?.message || 'Failed to fetch document';
    //showToast.error(errorMessage);
    return null;
  }
};

export const getPartnersList = async (
  categories: string[] = []
): Promise<string[]> => {
  const cacheKey = `partners_names_${categories.sort().join(',')}`;
  const now = Date.now();

  // Check cache
  if (
    cache[cacheKey] &&
    now - cache[cacheKey].timestamp < CACHE_DURATION
  ) {
    console.log('Returning cached partner names');
    return cache[cacheKey].data;
  }

  try {
    const categoryQuery =
      categories.length > 0
        ? `&category=${encodeURIComponent(categories.join(','))}`
        : '';

    console.log('Fetching partner names from API');

    const response = await axios.get<{ names: string[] }>(
      `/adminusers/v1/names/list?type=partner${categoryQuery}`,
      {
        headers: {
          'Content-Type': 'application/json',
          token: localStorage.getItem('token') || ''
        }
      }
    );

    if (Array.isArray(response.data?.names)) {
      const names = response.data.names
        .filter(name => name && name.trim() !== '')
        .map(name => name.trim());

      cache[cacheKey] = {
        data: names,
        timestamp: now
      };

      return names;
    }

    return [];
  } catch (error) {
    console.error('Error fetching partner names:', error);
    throw error;
  }
};


/**
 * Update a merchant's details
 * @param mobileNo Merchant's mobile number
 * @param updatedData Updated merchant data
 * @param token Authentication token
 * @returns Promise indicating success
 */
export const updateMerchant = async (
  mobileNo: string,
  updatedData: Partial<Merchant>,
  token: string | null
): Promise<boolean> => {
  try {
    console.log(`[API] Updating merchant with mobile: ${mobileNo}`, updatedData);

    if (!token) {
      console.error('[API] Missing authentication token for merchant update');
      showToast.error('Authentication required to update merchant');
      return false;
    }

    const endpoint = `/adminusers/v1/merchant/${mobileNo}`;
    console.log(`[API] Using endpoint for update: ${endpoint}`);

    const response = await axios.put(endpoint, updatedData, {
      headers: {
        'Content-Type': 'application/json',
        'token': token
      }
    });

    console.log('[API] Update merchant response:', response.data);

    if (response.data?.status === "Request successful!" ||
      response.data?.status === "Updated!") {
      showToast.success('Merchant updated successfully');
      return true;
    } else {
      console.warn('[API] Update merchant returned unexpected status:', response.data);
      showToast.error(response.data?.message || 'Failed to update merchant');
      return false;
    }
  } catch (error: any) {
    console.error('[API] Error updating merchant:', error.response?.data || error.message);
    const errorMessage = error.response?.data?.message || 'Failed to update merchant';
    showToast.error(errorMessage);
    return false;
  }
};


/**
 * Update merchant by merchantId (not MongoDB ID)
 * @param merchantId The merchant's unique merchantId 
 * @param data Update data
 * @param token Auth token
 * @returns Promise indicating success
 */
export const updateMerchantByMerchantId = async (
  merchantId: string,
  data: Partial<Merchant>,
  token: string | null
): Promise<boolean> => {
  try {
    console.log(`[API] Updating merchant with merchantId: ${merchantId}`, data);

    if (!token) {
      console.error('[API] Missing authentication token for merchant update');
      showToast.error('Authentication required to update merchant');
      return false;
    }

    const endpoint = `/adminusers/v1/merchant/${merchantId}`;
    console.log(`[API] Using endpoint for merchant update: ${endpoint}`);

    const response = await axios.put(endpoint, data, {
      headers: {
        'Content-Type': 'application/json',
        'token': token
      }
    });

    console.log('[API] Update merchant by merchantId response:', response.data);

    if (response.data?.status === "Request successful!" ||
      response.data?.status === "Updated!") {
      showToast.success('Merchant updated successfully');
      return true;
    } else {
      console.warn('[API] Update merchant by merchantId returned unexpected status:', response.data);
      showToast.error(response.data?.message || 'Failed to update merchant');
      return false;
    }
  } catch (error: any) {
    console.error('[API] Error updating merchant by merchantId:', error.response?.data || error.message);
    const errorMessage = error.response?.data?.message || 'Failed to update merchant';
    showToast.error(errorMessage);
    return false;
  }
};

export const createMerchant = async (
  payload: CreateMerchantPayload
): Promise<Merchant | null> => {
  try {
    console.log('[API] Creating merchant with payload:', payload);

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('[API] Missing authentication token for creating merchant');
      showToast.error('Authentication required to create merchant');
      return null;
    }

    const endpoint = `/adminusers/v1/merchant/create`;
    const response = await axios.post<CreateMerchantResponse>(
      endpoint,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          token,
        },
      }
    );

    const responseData = response.data;

    if (response.status === 201) {
      console.log('[API] Merchant created:', responseData.data);
      return responseData.data;
    } else {
      // ❌ Don't show toast here
      return null;
    }
  } catch (error: any) {
    console.error('[API] Error creating merchant:', error.response?.data || error.message);
    throw error; // ✅ Throw so it can be handled properly in handleSubmit
  }
};

export const createMerchantsBulk = async (
  file: File,
  afpId: string,
  kyc: 'byadmin' | 'bymerchant'
): Promise<{ created: number } | void> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      showToast.error('Authentication required');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const endpoint = `/adminusers/v1/merchant/bulk-create?partnerName=${encodeURIComponent(
      afpId
    )}&kyc=${kyc}`;

    const response = await axios.post(endpoint, formData, {
      headers: { 'Content-Type': 'multipart/form-data', token },
      responseType: 'blob', // handle both Excel and JSON
    });
    console.log('Bulk upload response :', response);

    const contentType = response.headers['content-type'];
    console.log('Response content-type:', contentType);

    if (
      contentType?.includes(
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    ) {
      // Skipped rows → download Excel
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `skipped_users_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.warning(
        'Some rows were skipped. Please check the downloaded Excel file.'
      );
      return;
    }

    // JSON → all rows created
    const text = await response.data.text();
    const json = JSON.parse(text);

    return { created: json.result?.created ?? 0 };
  } catch (error: any) {
    console.error('[API] Bulk merchant upload failed:', error.response?.data || error.message);
    showToast.error('Bulk merchant upload failed');
  }
};






/**
 * Uploads a KYC document for a merchant/partner/gatewaypartner.
 *
 * @param file - The file to upload (PDF, PNG, JPEG).
 * @param type - The user type (e.g., "merchant", "partner", etc.).
 * @param identifier - Either user ID or mobile number to identify the user.
 * @param docName - Name of the document being uploaded (must match one from `kycDoc` enum).
 * @param byPhone - If true, will use mobile number instead of ID.
 */
export const handleDocumentUpload = async (
  file: File,
  type: string,
  identifier: string,
  docName: string,
  byPhone: boolean = false
): Promise<any | null> => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      showToast.error("Authentication required");
      return null;
    }

    const formData = new FormData();
    formData.append("file", file);

    const endpoint = `/adminusers/${type}/docs/${docName}/${identifier}`;
    const response = await axios.put(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        token,
      },
    });

    if (response.status === 200) {
      showToast.success("Document uploaded successfully");
      return { user: response.data.user, status: response.status };
    } else {
      showToast.error("Failed to upload document");
      return null;
    }
  } catch (error: any) {
    const errMsg = error?.response?.data?.message || error.message;
    console.error("[API] Document upload failed:", errMsg);
    showToast.error(errMsg || "Document upload failed");
    return null;
  }
};
